import { api } from '../client';

import type {
  B2BShipmentDetailResponse,
  CancelB2BShipmentRequest,
  ConfirmB2BShipmentRequest,
  CreateB2BShipmentRequest,
  ReviseB2BShipmentRequest,
  UpdateB2BShipmentDraftRequest,
} from './b2bSales';
import {
  rememberResponseUpdatedAt,
  type StockDocumentMutationDeps,
  type StockDocumentMutationOptions,
} from './documentMutationCache';

type StockB2BShipmentsApiDeps = StockDocumentMutationDeps<'b2b_sale' | 'b2b_shipment'>;

function rememberB2BShipmentResponse(
  rememberStockDocumentUpdatedAt: StockB2BShipmentsApiDeps['rememberStockDocumentUpdatedAt'],
  response: B2BShipmentDetailResponse,
): B2BShipmentDetailResponse {
  return rememberResponseUpdatedAt(
    rememberStockDocumentUpdatedAt,
    'b2b_shipment',
    response,
    (current) => current.shipment.id,
    (current) => current.shipment.updated_at,
  );
}

export const createStockB2BShipmentsApi = ({
  rememberStockDocumentUpdatedAt,
  forgetStockDocumentUpdatedAt,
  withExpectedUpdatedAt,
}: StockB2BShipmentsApiDeps) => ({
  create: (
    saleId: string,
    data: CreateB2BShipmentRequest,
    options?: StockDocumentMutationOptions,
  ): Promise<B2BShipmentDetailResponse> =>
    api.webAdmin
      .post<B2BShipmentDetailResponse>(
        `/stock/b2b-sales/${saleId}/shipments`,
        data,
        withExpectedUpdatedAt('b2b_sale', saleId, options),
      )
      .then((response) => rememberB2BShipmentResponse(rememberStockDocumentUpdatedAt, response)),

  update: (
    saleId: string,
    shipmentId: string,
    data: UpdateB2BShipmentDraftRequest,
    options?: StockDocumentMutationOptions,
  ): Promise<B2BShipmentDetailResponse> =>
    api.webAdmin
      .put<B2BShipmentDetailResponse>(
        `/stock/b2b-sales/${saleId}/shipments/${shipmentId}`,
        data,
        withExpectedUpdatedAt('b2b_shipment', shipmentId, options),
      )
      .then((response) => rememberB2BShipmentResponse(rememberStockDocumentUpdatedAt, response)),

  confirm: (
    saleId: string,
    shipmentId: string,
    data: ConfirmB2BShipmentRequest = {},
    options?: StockDocumentMutationOptions,
  ): Promise<B2BShipmentDetailResponse> =>
    api.webAdmin
      .post<B2BShipmentDetailResponse>(
        `/stock/b2b-sales/${saleId}/shipments/${shipmentId}/confirm`,
        data,
        withExpectedUpdatedAt('b2b_shipment', shipmentId, options),
      )
      .then((response) => rememberB2BShipmentResponse(rememberStockDocumentUpdatedAt, response)),

  revise: (
    saleId: string,
    shipmentId: string,
    data: ReviseB2BShipmentRequest,
    options?: StockDocumentMutationOptions,
  ): Promise<B2BShipmentDetailResponse> =>
    api.webAdmin
      .post<B2BShipmentDetailResponse>(
        `/stock/b2b-sales/${saleId}/shipments/${shipmentId}/revise`,
        data,
        withExpectedUpdatedAt('b2b_shipment', shipmentId, options),
      )
      .then((response) => rememberB2BShipmentResponse(rememberStockDocumentUpdatedAt, response)),

  cancel: (
    saleId: string,
    shipmentId: string,
    data: CancelB2BShipmentRequest = {},
    options?: StockDocumentMutationOptions,
  ): Promise<B2BShipmentDetailResponse> =>
    api.webAdmin
      .post<B2BShipmentDetailResponse>(
        `/stock/b2b-sales/${saleId}/shipments/${shipmentId}/cancel`,
        data,
        withExpectedUpdatedAt('b2b_shipment', shipmentId, options),
      )
      .then((response) => rememberB2BShipmentResponse(rememberStockDocumentUpdatedAt, response)),

  deleteDraft: (
    saleId: string,
    shipmentId: string,
    options?: StockDocumentMutationOptions,
  ): Promise<{ request_id: string; deleted: boolean }> =>
    api.webAdmin
      .delete<{
        request_id: string;
        deleted: boolean;
      }>(
        `/stock/b2b-sales/${saleId}/shipments/${shipmentId}`,
        withExpectedUpdatedAt('b2b_shipment', shipmentId, options),
      )
      .then((response) => {
        if (response.deleted) {
          forgetStockDocumentUpdatedAt('b2b_shipment', shipmentId);
        }
        return response;
      }),
});
